import type { BrandScope } from "src/utils/brand-scope";
import errorImage from "src/assets/placeholders/error.png";
import { LOAD_ERROR_MESSAGE } from "src/pages/projects/constants";
import { useScrollFade } from "src/hooks/use-scroll-fade";
import { useTemplateFolderPicker } from "./use-template-folder-picker";
import type { TemplateSelection } from "./ad-studio-template-selection";
import { useState } from "react";
import {
  Box,
  Checkbox,
  Column,
  EmptyState,
  Breadcrumbs,
  Button,
  CloseIcon,
  Row,
  SearchInput,
  Text,
} from "@hightouch/htui";

import {
  ASSET_CARD_ASPECT_RATIO,
  AssetCardFooter,
  AssetCardMedia,
  AssetGrid,
  AssetPickerFooter,
  FolderTileGrid,
  SelectableAssetCard,
} from "src/components/asset-picker";
import { AdStudioTemplatePreview } from "src/pages/ad-studio/components/ad-studio-template-preview";
import {
  type AdStudioTemplateCard,
  type AdStudioTemplateDraft,
  isAdStudioTemplateCard,
  isPublishedAdStudioTemplate,
} from "src/pages/ad-studio/templates/types";

import { TallFormModal } from "src/components/modals/tall-form-modal";

type AdStudioTemplatePickerModalProps = {
  templates: AdStudioTemplateCard[];
  drafts: (AdStudioTemplateDraft | AdStudioTemplateCard)[];
  initialSelection: TemplateSelection;
  brandScope?: BrandScope;
  onClose: () => void;
  onConfirm: (selection: TemplateSelection) => void;
};

export function AdStudioTemplatePickerModal({
  templates,
  drafts,
  initialSelection,
  brandScope = null,
  onClose,
  onConfirm,
}: AdStudioTemplatePickerModalProps) {
  const selectionScrollFade = useScrollFade();
  const [selectedIds, setSelectedIds] = useState(initialSelection.ids);

  const folderPicker = useTemplateFolderPicker({
    templates: [...templates, ...drafts],
    initialSelection,
    selectedTemplateIds: selectedIds,
    brandScope,
  });
  const availableIds = new Set(
    folderPicker.availableTemplates.map((template) => template.id),
  );
  const validSelectedIds = selectedIds.filter((id) => availableIds.has(id));
  const browsedFolder = folderPicker.browsedFolder;
  const selectionCount =
    validSelectedIds.length + folderPicker.selectedFolderIds.length;

  const selectedTemplates = validSelectedIds.flatMap((id) => {
    const template = templates.find((candidate) => candidate.id === id);
    return template ? [template] : [];
  });

  const toggleTemplate = (id: string) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id],
    );
  };

  if (folderPicker.status !== "ready") {
    return (
      <TallFormModal
        isOpen
        title="Select templates"
        maxWidth="6xl"
        onClose={onClose}
        footer={<Button onClick={onClose}>Cancel</Button>}
      >
        {folderPicker.status === "error" ? (
          <EmptyState
            variant="minimal"
            imageUrl={errorImage}
            title="Could not load template folders"
            message={LOAD_ERROR_MESSAGE}
            actions={
              <Button onClick={() => void folderPicker.refetch()}>
                Try again
              </Button>
            }
          />
        ) : (
          <Text role="status">Loading template folders…</Text>
        )}
      </TallFormModal>
    );
  }

  return (
    <TallFormModal
      isOpen
      innerScroll
      maxWidth="6xl"
      title="Select templates"
      onClose={onClose}
      footer={
        <Column width="100%" gap={3}>
          <Row
            gap={2}
            align="center"
            flexWrap="wrap"
            maxHeight={24}
            overflowY="auto"
            ref={selectionScrollFade.ref}
            sx={selectionScrollFade.sx}
            aria-label="Selected templates and folders"
          >
            <Text size="sm" color="text.secondary">
              {selectionCount
                ? `${selectionCount} selected`
                : "Nothing selected"}
            </Text>
            {folderPicker.options
              .filter((folder) => folder.selected)
              .map((folder) => (
                <Button
                  key={folder.id}
                  size="sm"
                  aria-label={`Remove folder ${folder.label}`}
                  onClick={() => folderPicker.toggleFolder(folder.id)}
                >
                  {folder.label} ({folder.count} published)
                  <Box as={CloseIcon} boxSize={3} ml={2} />
                </Button>
              ))}
            {selectedTemplates.map((template) => (
              <Button
                key={template.id}
                size="sm"
                aria-label={`Remove template ${template.name}`}
                onClick={() => toggleTemplate(template.id)}
              >
                {template.name}
                <Box as={CloseIcon} boxSize={3} ml={2} />
              </Button>
            ))}
          </Row>
          <Row justify="flex-end">
            <AssetPickerFooter
              onCancel={onClose}
              onConfirm={() =>
                onConfirm({
                  ids: validSelectedIds,
                  ...(folderPicker.selectedFolderIds.length
                    ? {
                        folderIds: folderPicker.selectedFolderIds,
                        folderBrandProfileId: folderPicker.folderBrandProfileId,
                      }
                    : {}),
                })
              }
              confirmLabel={
                selectionCount > 0
                  ? `Select ${selectionCount} ${folderPicker.selectedFolderIds.length ? (selectionCount === 1 ? "item" : "items") : selectionCount === 1 ? "template" : "templates"}`
                  : "Done"
              }
              isConfirmDisabled={false}
            />
          </Row>
        </Column>
      }
    >
      <Column gap={4} flex={1} minHeight={0}>
        <Row gap={4} align="center" justify="space-between" flexWrap="wrap">
          <Breadcrumbs
            aria-label="Folder path"
            items={folderPicker.breadcrumbs}
          />
          <SearchInput
            aria-label="Search templates and folders"
            placeholder="Search templates and folders"
            value={folderPicker.search}
            onChange={(event) => folderPicker.setSearch(event.target.value)}
          />
        </Row>
        {browsedFolder && (
          <Column gap={2}>
            <Checkbox
              label="Select this folder"
              isChecked={browsedFolder.selected}
              isDisabled={browsedFolder.count === 0 && !browsedFolder.selected}
              onChange={() => folderPicker.toggleFolder(browsedFolder.id)}
            />
            <Text
              size="sm"
              color="text.secondary"
            >{`${browsedFolder.count} published · ${browsedFolder.draftCount} draft, including subfolders. Only published templates will be used.`}</Text>
            {browsedFolder.count === 0 && (
              <Text size="sm" color="text.secondary">
                This folder has no published templates to select.
              </Text>
            )}
          </Column>
        )}
        <Column gap={4} flex={1} minHeight={0} overflowY="auto" p={1}>
          <FolderTileGrid
            folders={folderPicker.folderTiles}
            onSelect={folderPicker.browseFolder}
          />
          <AssetGrid>
            {folderPicker.visibleTemplates.map((template) =>
              isPublishedAdStudioTemplate(template) ? (
                <TemplateAssetCard
                  key={template.id}
                  template={template}
                  selected={
                    selectedIds.includes(template.id) ||
                    folderPicker.includedTemplateIds.has(template.id)
                  }
                  includedByFolder={folderPicker.includedTemplateIds.has(
                    template.id,
                  )}
                  onToggle={() => toggleTemplate(template.id)}
                />
              ) : (
                <DraftTemplatePreview key={template.id} template={template} />
              ),
            )}
          </AssetGrid>
          {folderPicker.folderTiles.length === 0 &&
            folderPicker.visibleTemplates.length === 0 && (
              <EmptyState
                variant="minimal"
                title={folderPicker.search ? "No matches" : "No templates"}
                message={
                  folderPicker.search
                    ? "Try a different search term."
                    : "There are no templates for this brand in this folder."
                }
              />
            )}
        </Column>
      </Column>
    </TallFormModal>
  );
}

type TemplateAssetCardProps = {
  template: AdStudioTemplateCard;
  selected: boolean;
  includedByFolder: boolean;
  onToggle: () => void;
};

function TemplateAssetCard({
  template,
  selected,
  includedByFolder,
  onToggle,
}: TemplateAssetCardProps) {
  const { preview } = template.content;

  return (
    <Box sx={{ aspectRatio: ASSET_CARD_ASPECT_RATIO }}>
      <SelectableAssetCard
        selected={selected}
        checkboxLabel={template.name}
        onToggleSelect={onToggle}
        checkboxDisabled={includedByFolder}
        onClick={includedByFolder ? undefined : onToggle}
        footer={
          <AssetCardFooter
            title={template.name}
            subtitle={
              includedByFolder
                ? "Included from selected folder"
                : `${preview.width} × ${preview.height}`
            }
          />
        }
      >
        <AssetCardMedia>
          <AdStudioTemplatePreview template={template} />
        </AssetCardMedia>
      </SelectableAssetCard>
    </Box>
  );
}

const DraftTemplatePreview = ({
  template,
}: {
  template: AdStudioTemplateDraft | AdStudioTemplateCard;
}) => (
  <Column
    sx={{ aspectRatio: ASSET_CARD_ASPECT_RATIO }}
    borderWidth="hairline"
    borderColor="base.border"
    borderRadius="2xl"
    overflow="hidden"
  >
    <Column
      flex={1}
      minHeight={0}
      align="center"
      justify="center"
      bg="base.lightBackground"
      gap={2}
      p={3}
    >
      {isAdStudioTemplateCard(template) && (
        <AdStudioTemplatePreview template={template} />
      )}
      <Text fontWeight="medium">Draft</Text>
      <Text size="sm" color="text.secondary">
        Publish this template to use it
      </Text>
    </Column>
    <Box px={3} py={2}>
      <AssetCardFooter title={template.name} subtitle="Draft" />
    </Box>
  </Column>
);
